import { partition, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { Hanger, StandardConnectingHardwareModel } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import { RootState } from '..';

const getHangersAssociatedWithBearing = createSelector(
  (state: RootState) => state.jackGirderConnections.data,
  (state: RootState) => state.componentDesigns.data,
  (jackGirderConnections, componentDesigns) =>
    (bearingGuid: string): { hanger: Hanger | null; hangerCarriedItemConfigurationGuid: string | null }[] => {
      const associatedJackGirderConnections = Object.values(jackGirderConnections).filter(
        ({ jackBearingGuid }) => jackBearingGuid === bearingGuid,
      );

      const associatedHangers = associatedJackGirderConnections
        .map(({ hangerGuid, hangerCarriedItemConfigurationGuid, girderGuid }) => {
          if (hangerGuid == null) return { hanger: null, hangerCarriedItemConfigurationGuid: null };

          const girder = componentDesigns[girderGuid];
          if (girder == null) return null;

          const hanger = girder.component.hangers.find(({ guid }) => guid === hangerGuid);
          if (hanger == null) return null;

          return { hanger, hangerCarriedItemConfigurationGuid };
        })
        .filter(notNullish);

      return associatedHangers;
    },
);

export const getHangerModelsAssociatedWithBearing = createSelector(
  getHangersAssociatedWithBearing,
  (getHangersAssociatedWithBearing) => (bearingGuid: string) => {
    const connectingHardwaresAssociatedWithBearing = getHangersAssociatedWithBearing(bearingGuid)
      .map(({ hanger }) => hanger?.connector)
      .filter(notNullish);

    const [customHardware, standardHardware] = partition(
      connectingHardwaresAssociatedWithBearing,
      (hardware) => hardware.standardOrCustom === 'Custom',
    );
    return {
      customModels: uniq(customHardware.map(({ name }) => name)),
      standardModels: uniq(standardHardware.map(({ model }) => model)),
    };
  },
);

export const getHangerToDisplayOnBearing = createSelector(
  getHangerModelsAssociatedWithBearing,
  (getHangerModelsAssociatedWithBearing) => (bearingGuid: string) => {
    const { customModels, standardModels } = getHangerModelsAssociatedWithBearing(bearingGuid);
    if (customModels.length > 0 || standardModels.length !== 1) return null;
    const uniqueHardware = standardModels[0];

    return showHardwareOnJack(uniqueHardware) ? uniqueHardware : null;
  },
);

function showHardwareOnJack(hardware: StandardConnectingHardwareModel) {
  switch (hardware) {
    // These hangers meet one or more of the following criteria:
    // 1. The hangers have multiple configurations with different geometries
    // 2. The hangers can bear jacks at angles other than normal to the girder
    // We do not currently support rendering jack hangers taking these criteria into consideration, so we choose not to
    // render them to avoid confusing the user with misplaced visuals.
    case 'LTHJA26':
    case 'THJA26':
    case 'TJC37':
      return false;
    default:
      return true;
  }
}
